import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, message, Spin, Alert, Modal } from 'antd';
import { reportAPI } from '../services/api';
import { isGuestUser, saveGuestReport } from '../utils/guestUser';
import './DetectionPage.css';

const DetectionPage = ({ currentUser, setCurrentUser }) => {
  const navigate = useNavigate();

  // 如果没有用户，重定向到首页
  useEffect(() => {
    if (!currentUser) {
      message.warning('请先登录或使用游客模式');
      navigate('/');
    }
  }, [currentUser, navigate]);
  const [tongueImage, setTongueImage] = useState(null);
  const [faceImage, setFaceImage] = useState(null);
  const [tonguePreview, setTonguePreview] = useState(null);
  const [facePreview, setFacePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // 处理舌诊图片上传
  const handleTongueUpload = (file) => {
    setTongueImage(file);
    
    // 生成预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setTonguePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    return false; // 阻止自动上传
  };

  // 处理面诊图片上传
  const handleFaceUpload = (file) => {
    setFaceImage(file);
    
    // 生成预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setFacePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    return false; // 阻止自动上传
  };

  // 提交检测
  const handleSubmit = async () => {
    // 检查是否有缺失的照片
    const missingImages = [];
    if (!tongueImage) missingImages.push('舌诊照片');
    if (!faceImage) missingImages.push('面诊照片');

    // 如果有缺失的照片，弹窗提示并阻止请求
    if (missingImages.length > 0) {
      Modal.warning({
        title: '⚠️ 请上传检测照片',
        content: (
          <div style={{ padding: '12px 0' }}>
            <p style={{ marginBottom: '16px', fontSize: '15px', lineHeight: '1.8', color: '#3a4a45' }}>
              中医体质检测需要通过<strong style={{ color: '#296fab' }}>舌诊</strong>和<strong style={{ color: '#296fab' }}>面诊</strong>相结合，才能准确判断您的体质类型。
            </p>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #fff5f5 0%, #fef8f8 100%)', 
              padding: '16px', 
              borderRadius: '4px',
              border: '1px solid #ffb3b3',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500', color: '#c0392b' }}>
                ❌ 以下照片尚未上传：
              </p>
              <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '14px', color: '#3a4a45' }}>
                {missingImages.map((img, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{img}</li>
                ))}
              </ul>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f5f9f8 0%, #eef5f3 100%)',
              padding: '16px',
              borderRadius: '4px',
              border: '1px solid rgba(112, 174, 146, 0.3)'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500', color: '#70ae92' }}>
                💡 拍摄要求：
              </p>
              <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '13px', color: '#3a4a45', lineHeight: '1.8' }}>
                <li><strong>舌诊：</strong>自然伸出舌头，保持舌面平整，光线充足</li>
                <li><strong>面诊：</strong>面部清晰可见，表情自然，正面拍摄无遮挡</li>
              </ul>
            </div>
          </div>
        ),
        okText: '我知道了',
        className: 'detection-warning-modal',
        centered: true,
        width: 480,
      });
      return; // 阻止继续执行，不发送请求
    }

    // 两张照片都已上传，开始分析
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('user_id', currentUser.id);
      formData.append('tongue_image', tongueImage);
      formData.append('face_image', faceImage);

      const response = await reportAPI.createReport(formData);
      const report = response.data.report;

      // 如果是游客，额外保存到localStorage
      if (isGuestUser(currentUser)) {
        const guestReport = {
          ...report,
          isGuest: true,
          savedAt: new Date().toISOString()
        };
        saveGuestReport(guestReport);
        message.success('检测完成！报告已临时保存到浏览器');
      } else {
        message.success('检测完成！');
      }

      navigate(`/result/${report.id}`);
    } catch (error) {
      message.error('检测失败，请重试');
      console.error('检测错误:', error);
    } finally {
      setLoading(false);
    }
  };

  // 游客升级 - 引导返回首页并自动打开注册弹窗
  const handleUpgradeToUser = () => {
    Modal.confirm({
      title: '注册账号',
      content: '注册后可永久保存检测数据，并在任何设备上查看。是否返回首页注册？',
      okText: '返回首页注册',
      cancelText: '继续游客体验',
      onOk: () => {
        navigate('/?action=register');
      }
    });
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="container">
      <div className="page-wrapper detection-page">
        <h1 className="page-title">体质检测</h1>

        {isGuestUser(currentUser) && (
          <Alert
            message="游客模式提醒"
            description={
              <div>
                <p>您当前处于游客模式，报告将临时保存在浏览器中。</p>
                <p>
                  <strong>建议注册账号</strong>以永久保存检测数据。
                  <a onClick={handleUpgradeToUser} style={{ marginLeft: '8px', cursor: 'pointer', color: '#b8860b' }}>
                    立即注册 →
                  </a>
                </p>
              </div>
            }
            type="warning"
            showIcon
            closable
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading} tip="AI正在分析中，请稍候...">
          <div className="upload-section">
            <div className="upload-item">
              <h3 className="upload-title">📸 舌诊照片</h3>
              <div className="upload-tips">
                <p>拍摄要求：</p>
                <ul>
                  <li>自然伸出舌头</li>
                  <li>保持舌面平整</li>
                  <li>光线充足，照片清晰</li>
                </ul>
              </div>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleTongueUpload}
              >
                <div className="upload-area">
                  {tonguePreview ? (
                    <img src={tonguePreview} alt="舌诊" className="preview-image" />
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📷</div>
                      <div className="upload-text">点击上传舌诊照片</div>
                    </div>
                  )}
                </div>
              </Upload>
            </div>

            <div className="upload-item">
              <h3 className="upload-title">👤 面诊照片</h3>
              <div className="upload-tips">
                <p>拍摄要求：</p>
                <ul>
                  <li>面部清晰可见</li>
                  <li>表情自然放松</li>
                  <li>正面拍摄，无遮挡</li>
                </ul>
              </div>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleFaceUpload}
              >
                <div className="upload-area">
                  {facePreview ? (
                    <img src={facePreview} alt="面诊" className="preview-image" />
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📷</div>
                      <div className="upload-text">点击上传面诊照片</div>
                    </div>
                  )}
                </div>
              </Upload>
            </div>
          </div>

          <div className="button-group">
            <button 
              className="button-primary" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? '分析中...' : '开始分析'}
            </button>
            <button 
              className="button-secondary" 
              onClick={() => navigate('/')}
              disabled={loading}
            >
              返回首页
            </button>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default DetectionPage;

